import { apiClient } from '@/services'

import type {
  AgentSettingsDraft,
  AgentSettingsDryRunResult,
  AgentSettingsDraftWrite,
  AgentSettingsClientScope,
  AgentSettingsPublishInput,
  AgentSettingsScope,
  AgentSettingsVersion,
  ConnectorPolicyView,
  FirmEffectiveAgentSettings,
} from '../types'

const base = (orgId: string): string =>
  `/${encodeURIComponent(orgId)}/agent-settings`

const queryOf = (values: Record<string, string | undefined>): string => {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined) query.set(key, value)
  }
  return query.toString()
}

/** Current server contract only: FIRM drafts and immutable versions. */
export const agentSettingsService = {
  listClientScopes(orgId: string): Promise<AgentSettingsClientScope[]> {
    return apiClient.get<AgentSettingsClientScope[]>(
      `${base(orgId)}/client-scopes`
    )
  },

  listDrafts(
    orgId: string,
    scope: AgentSettingsScope,
    scopeKey?: string
  ): Promise<AgentSettingsDraft[]> {
    return apiClient.get<AgentSettingsDraft[]>(
      `${base(orgId)}?${queryOf({ scope, scopeKey })}`
    )
  },

  listVersions(
    orgId: string,
    scope: AgentSettingsScope,
    scopeKey: string | undefined,
    phase: string,
    agentKey: string
  ): Promise<AgentSettingsVersion[]> {
    return apiClient.get<AgentSettingsVersion[]>(
      `${base(orgId)}/versions?${queryOf({ scope, scopeKey, phase, agentKey })}`
    )
  },

  getFirmPublishedEffective(
    orgId: string,
    phase: string,
    agentKey: string
  ): Promise<FirmEffectiveAgentSettings> {
    return apiClient.get<FirmEffectiveAgentSettings>(
      `${base(orgId)}/effective/firm?${queryOf({ phase, agentKey })}`
    )
  },

  getConnectorPolicy(orgId: string): Promise<ConnectorPolicyView> {
    return apiClient.get<ConnectorPolicyView>(`${base(orgId)}/connector-policy`)
  },

  saveDraft(
    orgId: string,
    input: AgentSettingsDraftWrite
  ): Promise<AgentSettingsDraft> {
    return apiClient.put<AgentSettingsDraft>(base(orgId), input)
  },

  dryRun(
    orgId: string,
    input: AgentSettingsDraftWrite
  ): Promise<AgentSettingsDryRunResult> {
    return apiClient.post<AgentSettingsDryRunResult>(
      `${base(orgId)}/dry-run`,
      input
    )
  },

  publish(
    orgId: string,
    input: AgentSettingsPublishInput
  ): Promise<AgentSettingsVersion> {
    return apiClient.post<AgentSettingsVersion>(`${base(orgId)}/publish`, input)
  },
}
